import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AlertTooltip } from '@openx/components-core/lib/AlertTooltip/AlertTooltip';
import { PlayIconWithBackground } from '@openx/components-core/lib/CreativeVideoPreview/components/PlayIconWithBackground';
import { useImaAd } from '@openx/components-core/lib/CreativeVideoPreview/useImaAd';
import { Loader } from '@openx/components-core/lib/Loader/Loader';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PreviewSection } from './PreviewSection';
import { VideoPreviewModal } from './VideoPreviewModal';

type VideoThumbnailProps = {
  markupPayload: string;
};

export const VideoThumbnail = ({ markupPayload }: VideoThumbnailProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isHoveringRef = useRef(false);
  const videoElRef = useRef<HTMLVideoElement | null>(null);
  const [showLoader, setShowLoader] = useState(false);
  // Latches true whenever the ad is not available (no markup or error).
  // Only cleared by onAdsManagerReady so it stays visible through loading transitions.
  const [isUnavailable, setIsUnavailable] = useState(!markupPayload);

  const { adContainerRef, adManagerRef, isLoading, isError } = useImaAd(markupPayload, {
    onAdsManagerReady: (manager, ima, container) => {
      setIsUnavailable(false);
      manager.addEventListener(ima.AdEvent.Type.STARTED, () => {
        manager.setVolume(0);

        const videoEl = container.querySelector<HTMLVideoElement>('video');
        if (videoEl) {
          videoEl.loop = true;
          videoElRef.current = videoEl;
        }

        if (!isHoveringRef.current) manager.pause();
      });
    },
  });

  // Only show the spinner after 300 ms so fast that loads don't cause a flash.
  useEffect(() => {
    if (!isLoading) {
      setShowLoader(false);
      return;
    }

    const timer = setTimeout(() => setShowLoader(true), 300);

    return () => clearTimeout(timer);
  }, [isLoading]);

  const hasMarkup = !!markupPayload;
  const isPreviewReady = hasMarkup && !isError && !isLoading;

  useEffect(() => {
    if (isError || !hasMarkup) {
      setIsUnavailable(true);
    }
  }, [isError, hasMarkup]);

  const handleOpen = (): void => {
    adManagerRef.current?.pause();
  };

  const handleMouseEnter = (): void => {
    isHoveringRef.current = true;
    adManagerRef.current?.resume();
  };

  const handleMouseLeave = (): void => {
    isHoveringRef.current = false;
    adManagerRef.current?.pause();

    if (videoElRef.current) videoElRef.current.currentTime = 0;
  };

  const interactiveProps = isPreviewReady
    ? ({ isInteractive: true, ariaLabel: t('Open video preview') } as const)
    : ({ isInteractive: false } as const);

  return (
    <PreviewSection
      {...interactiveProps}
      onOpen={handleOpen}
      renderModal={onClose => <VideoPreviewModal markupPayload={markupPayload} onClose={onClose} />}
    >
      {/* Always mounted so the adContainerRef stays non-null across markup changes and prevents blink on add/delete. */}
      <Box
        sx={{ position: 'relative', width: '100%', height: '100%' }}
        onMouseEnter={isPreviewReady ? handleMouseEnter : undefined}
        onMouseLeave={isPreviewReady ? handleMouseLeave : undefined}
      >
        <Box
          data-test="thumbnail-video"
          ref={adContainerRef}
          sx={{
            maxHeight: '100%',
            maxWidth: '100%',
            width: '100%',
            height: '100%',
            visibility: isUnavailable ? 'hidden' : 'visible',
          }}
        />

        {showLoader && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Loader size={20} noPadding />
          </Box>
        )}

        {isUnavailable && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AlertTooltip
              title={<Typography variant="caption">{t('Preview not available.')}</Typography>}
              icon={<PlayCircleOutlineIcon fontSize="medium" />}
              color={theme.palette.text.secondary}
            />
          </Box>
        )}

        {isPreviewReady && (
          <>
            <Box sx={{ position: 'absolute', inset: 0, zIndex: 2 }} />
            <IconButton
              aria-label={t('Play video')}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: 'text.secondary',
                zIndex: 3,
                pointerEvents: 'none',
              }}
            >
              <PlayIconWithBackground />
            </IconButton>
          </>
        )}
      </Box>
    </PreviewSection>
  );
};
